import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { MarketInfoTabTemplate as template } from './market-info-tab.template';
import { MarketInfoTabStyles as styles } from './market-info-tab.styles';
import { Select, TextField } from '@genesislcap/foundation-zero';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../../services/enum.service';
import { IProjectModalService, IVppaService, ICommonService } from '../../../../../services';
import {
  clearErrorUtil,
  displayErrorUtil,
  PROJECT_OPERATIONAL_STATUS,
  updateGuaranteedCOD,
} from '../../../../../utils';
import { IVppa, Project } from '../../../../../types';
import { logger } from '@genesislcap/foundation-comms';
import { formatDateLong } from '../../../../../utils/formatting';
import { notifyError } from '../../../../../utils/notify';
import { AutoFillInput } from '../../../../common/auto-fill-input/auto-fill-input';

const name = 'market-info-tab';

@customElement({
  name,
  template,
  styles,
})
export class MarketInfoTab extends FASTElement {
  @ICommonService commonService: ICommonService;
  @IProjectModalService projectModalService: IProjectModalService;
  @IVppaService vppaService: IVppaService;

  // Market Info Tab Elements
  @observable iso: Select;
  @observable settlementPoint: Select;
  @observable interPoint: AutoFillInput;
  @observable interVoltage: Select;
  @observable interQueueId: TextField;
  @observable interUtility: TextField;
  @observable targetCOD: TextField;
  @observable guaranteedCOD: TextField;
  @observable operationalProjectCOD: TextField;

  // Market Info Tab Form Values
  @observable isoValue: string = '';
  @observable settlementPointValue: string = '';
  @observable interPointValue: string = '';
  @observable interVoltageValue: string = '_345KV';
  @observable interQueueIdValue: string = '';
  @observable interUtilityValue: string = '';
  @observable targetCODValue: string = '';
  @observable guaranteedCODValue: string = '';
  @observable operationalProjectCODValue: string = '';

  @observable _hubZoneMatrix = [];
  @observable _resuretyInterPoints = [];
  @observable _ISO = [];
  @observable _settlementPoints = [];
  @observable _interPoints = [];
  // ENUMs
  @observable interconnectionVoltageEnum: Array<EnumTranslate>;
  @IEnumService enumService: IEnumService;

  public _requiredFormFields = [];
  private projectVppas: Array<IVppa> = [];
  @attr @observable projectOperationalStatus: PROJECT_OPERATIONAL_STATUS;
  @observable project: Project = null;
  @observable shouldUpdateKeyMetrics: boolean = false;

  connectedCallback() {
    super.connectedCallback();

    this._requiredFormFields = [
      this.iso,
      this.settlementPoint,
      this.interPoint.autofillElement,
      this.interUtility,
    ];

    this.loadEnums();

    // update Service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);

    this.attachedBlurEvent();
    this.getHubZoneMatrix();
    this.getResuretyInterPoints();
  }

  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.INTERCONNECTION_VOLTAGE).then((response) => {
      if (response.REPLY) {
        this.interconnectionVoltageEnum = response.REPLY;
      }
    });
  }

  private attachedBlurEvent() {
    this._requiredFormFields.forEach((field) => {
      if (field instanceof Select) {
        field.addEventListener('change', () => this.checkFieldValidity(field));
      } else if (field === this.targetCOD) {
        field.addEventListener('change', () => this.validateTargetCOD());
      } else if (field === this.operationalProjectCOD) {
        field.addEventListener('change', () => this.validateOperationalProjectCOD());
      } else if (field === this.interPoint.autofillElement) {
        this.interPoint.autofillElement.addEventListener('change', () =>
          this.checkFieldValidity(field),
        );
      }

      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });
  }

  public checkFormValidity() {
    this._requiredFormFields.forEach((field) => {
      this.checkFieldValidity(field);
    });
  }

  /**
   * Check each field's validity and show/remove UI errors
   * @param elm
   */
  private checkFieldValidity(elm: TextField | Select) {
    if (!elm.value) {
      displayErrorUtil(elm);
      this.removeFromValidList(elm);
    } else if (elm === this.guaranteedCOD) {
      this.guaranteedCODValueChanged();
    } else if (elm === this.operationalProjectCOD) {
      this.validateOperationalProjectCOD();
    } else if (elm === this.interPoint.autofillElement) {
      this.checkIfShouldUpdateKeyMetrics();
    } else if (elm !== this.targetCOD) {
      clearErrorUtil(elm);
      this.appendToValidList(elm);
    }
  }

  public autofillValueIdValid() {
    this.appendToValidList(this.interPoint.autofillElement);
    this.checkIfShouldUpdateKeyMetrics();
  }

  public autofillValueIdInvalid() {
    this.removeFromValidList(this.interPoint.autofillElement);
    this.checkIfShouldUpdateKeyMetrics();
  }

  public validateTargetCOD() {
    let errorMessage = '';
    if (this.projectModalService.modalMode === 'edit' && this.projectVppas) {
      this.projectVppas.forEach((vppa) => {
        if (
          vppa.VPPA_STATUS != 'DELETED' &&
          vppa.TENOR_START_DATE < new Date(this.targetCODValue).getTime()
        ) {
          if (!errorMessage) {
            errorMessage = `VPPA ${vppa.UNIQUE_ID} start date ${formatDateLong(
              new Date(vppa.TENOR_START_DATE).getTime(),
            )}`;
          } else {
            errorMessage += `, VPPA ${vppa.UNIQUE_ID} start date ${formatDateLong(
              new Date(vppa.TENOR_START_DATE).getTime(),
            )}`;
          }
        }
      });
    }
    if (!errorMessage) {
      this.appendToValidList(this.targetCOD);
      this.appendToValidList(this.guaranteedCOD);
      clearErrorUtil(this.guaranteedCOD);
      clearErrorUtil(this.targetCOD);
      this.guaranteedCODValue = updateGuaranteedCOD(this.targetCODValue);
    } else {
      errorMessage += ` is before project Target COD ${formatDateLong(
        new Date(this.targetCODValue).getTime(),
      )}`;
      displayErrorUtil(this.targetCOD, errorMessage);
      this.removeFromValidList(this.targetCOD);
    }
  }

  public validateOperationalProjectCOD() {
    let errorMessage = '';
    const todaysDate = new Date();
    const todaysDateTime = todaysDate.getTime();
    const selectedDate = new Date(this.operationalProjectCODValue);

    if (this.projectModalService.modalMode === 'edit') {
      if (this.projectVppas) {
        this.projectVppas.forEach((vppa) => {
          if (vppa.VPPA_STATUS != 'DELETED' && vppa.TENOR_START_DATE < selectedDate.getTime()) {
            if (!errorMessage) {
              errorMessage = `VPPA ${vppa.UNIQUE_ID} start date ${formatDateLong(
                new Date(vppa.TENOR_START_DATE).getTime(),
              )}`;
            } else {
              errorMessage += `, VPPA ${vppa.UNIQUE_ID} start date ${formatDateLong(
                new Date(vppa.TENOR_START_DATE).getTime(),
              )}`;
            }
          }
        });

        if (!errorMessage) {
          clearErrorUtil(this.operationalProjectCOD);
          this.appendToValidList(this.operationalProjectCOD);
        } else {
          errorMessage += ` is before project COD ${formatDateLong(
            new Date(this.operationalProjectCODValue).getTime(),
          )}`;
          displayErrorUtil(this.operationalProjectCOD, errorMessage);
          this.removeFromValidList(this.operationalProjectCOD);
        }
      }
      if (todaysDateTime < selectedDate.getTime()) {
        if (!errorMessage) {
          errorMessage = `COD must be less than or equals to ${formatDateLong(todaysDateTime)}.`;
        } else {
          errorMessage += `. COD must be less than or equals to ${formatDateLong(todaysDateTime)}.`;
        }
        displayErrorUtil(this.operationalProjectCOD, errorMessage);
        this.removeFromValidList(this.operationalProjectCOD);
      }
      if (!errorMessage) {
        clearErrorUtil(this.operationalProjectCOD);
        this.appendToValidList(this.operationalProjectCOD);
      }
    } else if (this.projectModalService.modalMode === 'create') {
      if (todaysDateTime < selectedDate.getTime()) {
        errorMessage = `COD must be less than or equals to ${formatDateLong(todaysDateTime)}.`;
        displayErrorUtil(this.operationalProjectCOD, errorMessage);
        this.removeFromValidList(this.operationalProjectCOD);
      }
      if (!errorMessage) {
        clearErrorUtil(this.operationalProjectCOD);
        this.appendToValidList(this.operationalProjectCOD);
      }
    }
  }

  /**
   * Adds fields to the service completed fields list
   * @param field
   */
  private appendToValidList(field: TextField | Select) {
    this.projectModalService.addToTotalCompletedFields(field);
    this.checkTabCompletionStatus();
  }

  /**
   * Removes fields from the service completed fields list
   * @param field
   */
  private removeFromValidList(field: TextField | Select) {
    this.projectModalService.removeFromTotalCompletedFields(field);
    this.checkTabCompletionStatus();
  }

  private getHubZoneMatrix() {
    this.commonService.getHubZoneMatrix().then((response) => {
      if (response.REPLY) {
        this._hubZoneMatrix = response.REPLY;
        this.generateISODD();
      }
    });
  }

  private getResuretyInterPoints() {
    this.commonService.getResuretyInterPoints().then((response) => {
      if (response.REPLY) {
        this._resuretyInterPoints = response.REPLY;
      }
    });
  }

  private generateISODD() {
    this._ISO = [...new Set(this._hubZoneMatrix.map((item) => item.ISO))];
  }

  private generateSettlementPointDD() {
    // Get all rows with selected ISO
    const selectedPoints = this._hubZoneMatrix.filter((item) => item.ISO === this.isoValue);

    // Get Unique Settlement Points for DD
    this._settlementPoints = [...new Set(selectedPoints.map((item) => item.SETTLEMENT_POINT))];

    this.generateInterPointDD();
  }

  private generateInterPointDD() {
    // Get all rows with selected ISO
    this._interPoints = this._resuretyInterPoints
      .filter((item) => item.ISO === this.isoValue)
      .sort((a, b) => a.NAME.localeCompare(b.NAME));
  }

  public isoSelected() {
    // Reset previously selected values
    this.settlementPointValue = '';
    this.interPoint.setResuretyLocationID('');
    this.projectModalService.removeFromTotalCompletedFields(this.settlementPoint);
    this.projectModalService.removeFromTotalCompletedFields(this.interPoint.autofillElement);
    this.clearErrors([this.settlementPoint, this.interPoint.autofillElement]);

    // Generate dropdown values
    if (this.isoValue) {
      this.interPoint.disabled = false;
      this.generateSettlementPointDD();
    } else {
      this.interPoint.disabled = true;
      this._interPoints = [];
    }
    this.interPoint.resetFilter();
  }

  private clearErrors(fields: Array<TextField | Select>) {
    fields.forEach((field) => {
      clearErrorUtil(field);
    });
  }

  private checkTabCompletionStatus() {
    const tabCompleted = this._requiredFormFields.every((elm) =>
      this.projectModalService.totalCompletedFields.includes(elm),
    );
    this.$emit('marketInfoTabCompleted', tabCompleted);
  }

  public clearForm() {
    const allFields = [
      this.iso,
      this.settlementPoint,
      this.interPoint.autofillElement,
      this.interVoltage,
      this.interUtility,
      this.interQueueId,
      this.targetCOD,
      this.guaranteedCOD,
      this.operationalProjectCOD,
    ];

    allFields.forEach((field) => {
      clearErrorUtil(field);
    });

    this.isoValue = '';
    this.settlementPointValue = '';
    this.interPointValue = '';
    this.interPoint.setResuretyLocationID('');
    this.interPoint.disabled = true;
    this.interUtilityValue = '';
    this.interQueueIdValue = '';
    this.targetCODValue = '';
    this.operationalProjectCODValue = '';
    this.guaranteedCODValue = '';
    this.interVoltageValue = '_345KV';
    this.shouldUpdateKeyMetrics = false;

    this.projectVppas = [];

    this._requiredFormFields = this._requiredFormFields.filter((elm) => elm !== this.targetCOD);

    this.$emit('marketInfoTabCompleted', false);
  }

  public populateForm(project: Project) {
    this.project = project;
    this.addRequiredFieldsToService();

    if (this.projectModalService.modalMode === 'edit') {
      if (project.PROJECT_ID) {
        this.vppaService.getVppas(project.PROJECT_ID).then((response) => {
          if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
            this.projectVppas = response.REPLY;
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response.ERROR);
          }
        });
      }

      if (project.ISO) {
        this.isoValue = project.ISO;
        this.interPoint.disabled = false;
        this.generateSettlementPointDD();
        this.appendToValidList(this.iso);
      }

      if (project.SETTLEMENT_POINT) {
        setTimeout(() => {
          this.settlementPointValue = project.SETTLEMENT_POINT;
          this.appendToValidList(this.settlementPoint);
        }, 100);
      }

      this.interVoltageValue = project.INTERCONNECTION_VOLTAGE;
    } else {
      if (project.ISO_STRING) {
        this.isoValue = project.ISO_STRING;
        this.interPoint.disabled = false;
        this.generateSettlementPointDD();
        this.appendToValidList(this.iso);
      }

      if (project.SETTLEMENT_POINT) {
        setTimeout(() => {
          this.settlementPointValue = project.SETTLEMENT_POINT;
          this.appendToValidList(this.settlementPoint);
        }, 100);
      }

      this.interVoltageValue = project.INTERCONNECTION_VOLTAGE_STRING;
    }

    if (project.INTERCONNECTION_POINT) {
      setTimeout(() => {
        this.interPoint.setResuretyLocationID(project.INTERCONNECTION_POINT);
        this.interPointValue = project.INTERCONNECTION_POINT;
        this.appendToValidList(this.interPoint.autofillElement);
      }, 100);
    }

    if (project.INTERCONNECTION_UTILITY) {
      this.interUtilityValue = project.INTERCONNECTION_UTILITY;
      this.appendToValidList(this.interUtility);
    }

    this.projectOperationalStatus = PROJECT_OPERATIONAL_STATUS[project.PROJECT_OPERATIONAL_STATUS];

    this.validateProjectOperationalStatus(this.projectOperationalStatus);

    if (this.projectOperationalStatus === PROJECT_OPERATIONAL_STATUS.NEW_BUILD) {
      if (project.TARGET_COD) {
        this.targetCODValue = new Date(project.TARGET_COD).toISOString().split('T')[0];
        this.appendToValidList(this.targetCOD);
      }
      if (project.GUARANTEED_COD) {
        this.guaranteedCODValue = new Date(project.GUARANTEED_COD).toISOString().split('T')[0];
        this.appendToValidList(this.guaranteedCOD);
      }
    }

    if (this.projectOperationalStatus === PROJECT_OPERATIONAL_STATUS.OPERATIONAL) {
      const todaysDate = new Date();
      if (project.COD) {
        const setDate = new Date(project.COD);
        this.operationalProjectCODValue = setDate.toISOString().split('T')[0];
        if (setDate.getTime() > todaysDate.getTime()) {
          displayErrorUtil(
            this.operationalProjectCOD,
            `COD must be less than or equals to ${formatDateLong(todaysDate.getTime())}.`,
          );
          this.removeFromValidList(this.operationalProjectCOD);
        } else {
          this.appendToValidList(this.operationalProjectCOD);
        }
      }
    }

    this.interQueueIdValue = project.INTERCONNECTION_QUEUE_ID;
  }

  public addRequiredFieldsToService() {
    // Update service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);
  }

  public guaranteedCODValueChanged() {
    if (this.guaranteedCODValue) {
      if (this.targetCODValue) {
        const guaranteedCodDateTime = new Date(this.guaranteedCODValue).getTime();
        const targetCodDateTime = new Date(this.targetCODValue).getTime();
        if (guaranteedCodDateTime <= targetCodDateTime) {
          displayErrorUtil(this.guaranteedCOD, 'Guaranteed COD must be greater than Target COD');
          this.removeFromValidList(this.guaranteedCOD);
        } else {
          clearErrorUtil(this.guaranteedCOD);
          this.appendToValidList(this.guaranteedCOD);
        }
      } else {
        displayErrorUtil(this.guaranteedCOD, 'Please complete Target COD');
        this.removeFromValidList(this.guaranteedCOD);
      }
    }
  }

  public projectOperationalStatusChanged(oldValue, newValue: PROJECT_OPERATIONAL_STATUS) {
    this.validateProjectOperationalStatus(newValue);
  }

  private validateProjectOperationalStatus(value: PROJECT_OPERATIONAL_STATUS) {
    if (value === PROJECT_OPERATIONAL_STATUS.NEW_BUILD) {
      this._requiredFormFields = this._requiredFormFields.filter(
        (elm) => elm !== this.operationalProjectCOD,
      );
      this._requiredFormFields.push(this.targetCOD);
      this._requiredFormFields.push(this.guaranteedCOD);
      this.projectModalService.removeFromRequiredFormField([this.operationalProjectCOD]);
      this.projectModalService.removeFromTotalCompletedFields(this.operationalProjectCOD);
      this.projectModalService.addToRequiredFormField(this._requiredFormFields);
      if (this.targetCODValue) {
        this.appendToValidList(this.targetCOD);
      }
      if (this.guaranteedCODValue) {
        this.appendToValidList(this.guaranteedCOD);
      }
      this.guaranteedCODValueChanged();
    } else {
      this._requiredFormFields = this._requiredFormFields.filter(
        (elm) => elm !== this.targetCOD && elm !== this.guaranteedCOD,
      );
      this._requiredFormFields.push(this.operationalProjectCOD);
      this.projectModalService.removeFromRequiredFormField([this.targetCOD, this.guaranteedCOD]);
      this.projectModalService.removeFromTotalCompletedFields(this.targetCOD);
      this.projectModalService.removeFromTotalCompletedFields(this.guaranteedCOD);
      this.projectModalService.addToRequiredFormField(this._requiredFormFields);
      if (this.operationalProjectCODValue) {
        this.appendToValidList(this.operationalProjectCOD);
      }
    }

    this.attachedBlurEvent();
    this.checkTabCompletionStatus();
  }

  private checkIfShouldUpdateKeyMetrics() {
    // Compare the current interconnection point id to the id of the original project
    if (this.projectModalService.modalMode === 'edit') {
      this.shouldUpdateKeyMetrics =
        this.interPoint.autofillValueID != this.project?.INTERCONNECTION_POINT;
    }
  }
}
